import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { Box, Button, Container, Grid, Stack } from '@mui/material';

import { MyParams, RouteEnum, generateRoutePath, useSession, API_ROUTES } from 'App';
import {
  BreadCrumbs,
  duration,
  HeadCell,
  Loader,
  ReviewIdentityDialog,
  Screenshots,
  SimpleTable,
  SimpleTimeline,
  useExamSessionReview,
  usePreSignedUrl,
  useScreenshotClocks,
  useToogle,
  Video,
  WRONG_VIDEO_LENGTH_VALUE,
  CONTACT_PROCTORFREE_SUPPORT_AUDIT_BASE_URL,
} from '@shared';

import { AuditTableCells, DownloadMenu, ScreenshotsDialog, SwitchAnomalies } from './components';
import { useVisibleAnomalies, useAnomalies } from './hooks';
import { ColumnsIds } from './Audit.types';
import { styled } from '@mui/material/styles';

const ActionsStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'collapseAt',
})<{ collapseAt?: number }>(({ collapseAt = 823 }) => ({
  '& > *': {
    flexGrow: 0,
    flexBasis: 'auto',
    minWidth: 'auto',
  },
  '& .MuiButton-root': {
    width: 'auto',
    whiteSpace: 'nowrap',
  },

  [`@media (max-width:${collapseAt}px)`]: {
    justifyContent: 'space-between',

    '& > *': {
      flexGrow: 1,
      flexBasis: '48%',
      minWidth: 160,
    },

    '& .MuiButton-root': {
      width: '100%',
    },
  },
}));

const headCells: readonly HeadCell<ColumnsIds>[] = [
  {
    id: 'anomalies',
    label: 'Anomalies',
  },
  {
    id: 'start',
    label: 'Start',
  },
  {
    id: 'end',
    label: 'End',
  },
  {
    id: 'label',
    label: 'Type',
  },
  {
    id: 'description',
    label: 'Description',
  },
];

export function Audit() {
  const { session } = useParams<keyof MyParams>() as MyParams;

  const { currentUser } = useSession();
  const { isLeadAuditor, isInstructor } = currentUser.roles;

  const [isPlaying, setIsPlaying] = useState(false);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [progressPlayed, setProgressPlayed] = useState(0);
  const playerRef = useRef<ReactPlayer>(null);
  const { isOpen, onOpen, onClose } = useToogle();

  const handleSeekChange = (seek: number, isPlay = true) => {
    setIsPlaying(isPlay);
    playerRef.current?.seekTo(seek);
  };

  const { data, isLoading, isError } = useExamSessionReview(session);

  const auditLink = generateRoutePath({
    name: RouteEnum.EnhancedAudit,
    params: { audit: data?.audits[0]?.uuid || '' },
  });

  const { data: screenshotClocks = [] } = useScreenshotClocks(data?.streamResponse.streamHandle);
  const { data: anomalies } = useAnomalies(data?.examSession.uuid, data?.audits[0]?.uuid);
  const [visibleAnomalies, dispatch] = useVisibleAnomalies(anomalies);

  const [isOpenModal, setIsOpenModal] = useState(false);

  const { preSignedUrl, refreshPreSignedUrl } = usePreSignedUrl(data?.muxedVideoHandle);

  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return null;
  }

  const videoDuration =
    (playerRef.current && playerRef.current.getDuration() * 1000) ||
    duration(data.streamResponse.videoStartClock, data.streamResponse.videoEndClock) -
      WRONG_VIDEO_LENGTH_VALUE;

  const breadcrumbsNameMap = [
    {
      path: generateRoutePath({ name: RouteEnum.Courses, params: { partner: data.partner.uuid } }),
      name: data.partner.name,
    },
    {
      path: generateRoutePath({
        name: RouteEnum.Exams,
        params: { partner: data.partner.uuid, course: data.course.uuid },
      }),
      name: data.course.name,
    },
    {
      path: generateRoutePath({
        name: RouteEnum.ExamSessions,
        params: { partner: data.partner.uuid, course: data.course.uuid, exam: data.exam.uuid },
      }),
      name: data.exam.name,
    },
    {
      path: generateRoutePath({
        name: RouteEnum.ExamAudit,
        params: { session },
      }),
      name: `${data.user.firstName} ${data.user.lastName}`,
    },
  ];

  const openReportPage = (auditLink: string) => {
    const auditUrl = `${window.location.origin}${auditLink}`;
    const encodedAuditUrl = encodeURIComponent(auditUrl);

    const finalUrl = `${CONTACT_PROCTORFREE_SUPPORT_AUDIT_BASE_URL}?audit_url=${encodedAuditUrl}`;

    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  };

  const roomScanTraceHandle = isLeadAuditor || isInstructor ? data?.roomScanTraceHandle : undefined;
  return (
    <Container component='article'>
      <BreadCrumbs breadcrumbNameMap={breadcrumbsNameMap} />
      <ActionsStack
        justifyContent='flex-end'
        direction='row'
        gap={1}
        alignItems='center'
        mb={1}
        flexWrap='wrap'
      >
        <Button variant='contained' color='error' onClick={() => openReportPage(auditLink)}>
          Report Audit
        </Button>
        {isLeadAuditor && (
          <Link to={auditLink}>
            <Button variant='outlined'>Review audit</Button>
          </Link>
        )}
        <Button variant='contained' onClick={() => setIsOpenModal(true)}>
          Review Identity
        </Button>
        <Button disabled={!screenshotClocks.length} variant='contained' onClick={onOpen}>
          View screenshots
        </Button>
        {(isLeadAuditor || isInstructor) && (
          <DownloadMenu
            screenshotsLink={API_ROUTES.apiV1AuditScreenshotZip(data.streamResponse.streamHandle)}
            videoLink={API_ROUTES.apiV1AuditVideoDownload(data.muxedVideoHandle)}
          />
        )}
      </ActionsStack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Video
            src={preSignedUrl}
            playing={isPlaying}
            setPlaying={setIsPlaying}
            onSetProgressPlayed={setProgressPlayed}
            playerRef={playerRef}
            playbackRate={playbackRate}
            onSetPlaybackRate={setPlaybackRate}
            onSetPlayedSeconds={setPlayedSeconds}
            playedSeconds={playedSeconds}
            regions={[]}
            onSeekChange={handleSeekChange}
            videoDuration={videoDuration}
            refreshPreSignedUrl={refreshPreSignedUrl}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Screenshots
            screenshotsClock={screenshotClocks}
            stream={data.streamResponse}
            progressPlayed={progressPlayed}
          />
        </Grid>
      </Grid>

      <ReviewIdentityDialog
        title='Review Identity'
        isModalOpen={isOpenModal}
        onCloseModal={() => setIsOpenModal(false)}
        documentTraceHandle={data.documentTraceHandle}
        profileTraceHandle={data.profileTraceHandle}
        roomScanTraceHandle={roomScanTraceHandle}
      />

      <SimpleTimeline
        anomalies={visibleAnomalies}
        playedSeconds={playedSeconds}
        progressPlayed={progressPlayed}
        videoDuration={videoDuration}
        onSeekChange={handleSeekChange}
        isPlaying={isPlaying}
      />
      <ScreenshotsDialog
        stream={data.streamResponse}
        isModalOpen={isOpen}
        onCloseModal={onClose}
        screenshotClocks={screenshotClocks}
      />
      <SwitchAnomalies anomalyTypeViews={data.anomalyTypeViews} onSwitch={dispatch} />

      <Box mt={2}>
        <SimpleTable
          columns={headCells}
          rows={visibleAnomalies}
          entityName={'anomaly'}
          /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
          renderElems={({ uuid, id, type, ...rest }) => (
            <AuditTableCells entries={rest} onSeekChange={handleSeekChange} />
          )}
        />
      </Box>
    </Container>
  );
}
